import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { brandingName } from '../../api/branding';
import { verifyDocument } from '../../api/document';
import type { Verification } from '../../api/document';
import { useBranding } from '../../config';
import { useSession } from '../../session/useSession';
import { AuthShell } from '../auth/AuthShell';
import { formatDate } from '../format';
import { Alert, CheckedForm, LIMITS, TextField, problemMessage } from '../form';
import { Icon } from '../Icon';
import { typeLabel, VerificationNote } from './shared';

/**
 * La page publique de vérification (D61) : l'adresse que porte le code QR
 * d'un relevé, d'un procès-verbal ou d'une attestation. Sans session.
 */
export function VerifyPage(): ReactNode {
    const { t } = useSession();
    const { reference = '' } = useParams();
    const navigate = useNavigate();
    const [typed, setTyped] = useState(reference);
    const [result, setResult] = useState<Verification | null>(null);
    const [failure, setFailure] = useState<string | null>(null);

    useEffect(() => {
        setTyped(reference);

        if (reference === '') {
            setResult(null);

            return undefined;
        }

        let abandoned = false;
        verifyDocument(reference)
            .then((r) => !abandoned && setResult(r))
            .catch((cause: unknown) => !abandoned && setFailure(problemMessage(t, cause)));

        return () => {
            abandoned = true;
        };
    }, [reference, t]);

    // Arrivé par le code QR : le verdict d'abord, lisible sur un téléphone (planche 269).
    if (reference !== '' && result !== null && failure === null) {
        return <VerifyResult result={result} />;
    }

    function submit(): void {
        setFailure(null);
        navigate(`/verify/${encodeURIComponent(typed.trim().toUpperCase())}`);
    }

    return (
        <AuthShell>
            <CheckedForm className="auth-form" onSubmit={submit}>
                <div className="auth-form__head">
                    <h1>{t('doc.verify.publicTitle')}</h1>
                    <span className="muted">{t('doc.verify.publicSub')}</span>
                </div>
                <TextField id="verify-reference" label={t('doc.field.reference')} limit={LIMITS.IssuedDocument.reference} required mono autoComplete="off" value={typed} onChange={setTyped} />
                <button className="button auth-form__submit" type="submit">
                    {t('doc.action.verify')}
                </button>
                {failure === null ? null : <Alert tone="error">{failure}</Alert>}
                {result === null ? null : <VerificationNote result={result} />}
            </CheckedForm>
        </AuthShell>
    );
}

const VERDICT_ICONS: Readonly<Record<Verification['state'], string>> = {
    valid: 'check',
    unsigned: 'clock',
    superseded: 'refresh',
    expired: 'clock',
    altered: 'alert',
    cancelled: 'ban',
    unknown: 'alert',
};

/** Le verdict, en grand, et ce que le document dit de lui-même, sans notes ni décision. */
function VerifyResult({ result }: { readonly result: Verification }): ReactNode {
    const { t, locale } = useSession();
    const { assets: brand, server } = useBranding();
    const name = brandingName(server, locale) ?? brand.name;
    const tone = result.state === 'valid' ? 'ok' : result.state === 'superseded' || result.state === 'expired' || result.state === 'unsigned' ? 'warn' : 'danger';
    const date = (value: string | null | undefined): string => formatDate(value ?? null, locale) ?? '';
    // D82 : un quitus de bibliothèque porte la catégorie du lecteur à la place de la formation.
    const clearance = (result.type as string | undefined) === 'library_clearance';
    const body =
        result.state === 'valid'
            ? clearance
                ? t('lib.verify.clearanceBody', { date: date(result.issuedAt) })
                : t('doc.verify.validBody')
            : result.state === 'unsigned'
              ? t('doc.verify.unsignedBody')
              : result.state === 'superseded'
                ? t('doc.verify.supersededBody', { date: date(result.supersededAt) })
                : result.state === 'cancelled'
                  ? t('doc.verify.cancelledBody', { date: date(result.cancelledAt) })
                  : result.state === 'expired'
                    ? t('doc.verify.expiredBody', { date: date(result.validUntil) })
                    : result.state === 'altered'
                      ? t('doc.verify.alteredBody')
                      : t('doc.verify.unknownBody', { reference: result.reference });
    const rows: [string, string | null | undefined][] =
        result.state === 'unknown'
            ? []
            : [
                  [t('doc.verify.row.type'), result.type === undefined ? null : typeLabel(t, result.type)],
                  [t('doc.verify.row.student'), result.student],
                  [clearance ? t('lib.verify.category') : t('doc.verify.row.training'), result.training],
                  [t('doc.verify.row.year'), result.year],
                  [t('doc.verify.row.issued'), date(result.issuedAt)],
                  [t('doc.verify.row.signed'), result.signedBy === null || result.signedBy === undefined ? null : `${result.signedBy} · ${date(result.signedAt)}`],
                  [t('doc.verify.row.until'), result.validUntil === null || result.validUntil === undefined ? null : date(result.validUntil)],
              ];

    return (
        <main className="vrf">
            <header className="vrf__head">
                {brand.logo === undefined ? null : <img className="vrf__logo" src={brand.logo} alt={name ?? ''} />}
                <span className="muted">{t('doc.verify.pageTitle')}</span>
            </header>
            <section className={`vrf__verdict vrf__verdict--${tone}`} role="status">
                <span className="vrf__mark">
                    <Icon name={VERDICT_ICONS[result.state]} size={30} />
                </span>
                <h1>{t(`doc.verify.${result.state}`)}</h1>
                <p>{body}</p>
            </section>
            {rows.length === 0 ? null : (
                <dl className="vrf__facts">
                    {rows
                        .filter((row): row is [string, string] => row[1] !== null && row[1] !== undefined && row[1] !== '')
                        .map(([label, value]) => (
                            <div key={label}>
                                <dt>{label}</dt>
                                <dd>{value}</dd>
                            </div>
                        ))}
                    <div className="vrf__ref">
                        <dd>{result.reference}</dd>
                    </div>
                </dl>
            )}
            <p className="vrf__note muted">{t('doc.verify.privacy')}</p>
            <Link className="com-link vrf__again" to="/verify">
                {t('doc.verify.another')}
            </Link>
        </main>
    );
}
